import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { RequestService } from '../../services/request.service';
import {
  EstadoLecturaNotificacionDTO,
  NotificacionUsuarioDTO,
} from '../../services/workspace/DTO/notificacion.dto';
import { NotificacionesWorkspaceService } from '../../services/workspace/notificaciones-workspace.service';
import {
  EstadoActividadUsuario,
  UsuariosActividadTrackerService,
} from '../../services/workspace/usuarios-actividad-tracker.service';

@Component({
  selector: 'app-notificaciones-flotantes',
  templateUrl: './notificaciones-flotantes.component.html',
  styleUrls: ['./notificaciones-flotantes.component.scss'],
})
export class NotificacionesFlotantesComponent
  implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('panelNotificaciones') panelRef: ElementRef;

  contador = 0;
  panelAbierto = false;
  cargandoLista = false;
  cargandoAccion = false;
  errorLista = '';
  estado: EstadoLecturaNotificacionDTO = 'TODAS';
  items: NotificacionUsuarioDTO[] = [];
  pagina = 1;
  limite = 20;
  total = 0;

  posicionX = 0;
  posicionY = 0;
  panelX = 12;
  panelY = 12;
  arrastrando = false;
  posicionLista = false;

  private suscripciones: Subscription[] = [];
  private claveSesion = '';
  private desplazado = false;
  private omitirClick = false;
  private inicioPunteroX = 0;
  private inicioPunteroY = 0;
  private inicioPosicionX = 0;
  private inicioPosicionY = 0;
  private socketSuspendido = false;
  private readonly tamanoBoton = 58;
  private readonly margenViewport = 14;

  /**
   * Recibe la fuente global de actividad y conserva en el componente solo la
   * adaptación visual, de sesión y transporte de las notificaciones.
   */
  constructor(
    private elementRef: ElementRef,
    private zone: NgZone,
    private router: Router,
    public requestServ: RequestService,
    private notificacionesServ: NotificacionesWorkspaceService,
    private actividadTracker: UsuariosActividadTrackerService
  ) {}

  /**
   * Enlaza la sesión autenticada con API y Socket.IO y consume la actividad
   * global; los eventos remotos solo invalidan datos del backend tenant.
   */
  ngOnInit(): void {
    this.suscripciones.push(
      this.notificacionesServ.cambios$.subscribe(() => {
        this.zone.run(() => this.procesarCambioRemoto());
      }),
      this.notificacionesServ.reconexion$.subscribe(() => {
        this.zone.run(() => this.recuperarTrasReconexion());
      }),
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(() => this.sincronizarSesion()),
      this.actividadTracker.estado$.subscribe(estado => {
        this.procesarEstadoActividad(estado);
      })
    );
    this.actividadTracker.iniciar();
    this.sincronizarSesion();
  }

  /**
   * Restaura la posición elegida por el usuario cuando el botón ya puede
   * medirse y la ajusta al viewport actual sin alterar datos de negocio.
   */
  ngAfterViewInit(): void {
    this.restaurarPosicion();
  }

  /**
   * Libera transporte y observadores del layout autenticado sin detener el
   * tracker global que también gobierna consultas periódicas del sistema.
   */
  ngOnDestroy(): void {
    this.suscripciones.forEach((suscripcion) => suscripcion.unsubscribe());
    this.notificacionesServ.desconectar();
  }

  /**
   * Cierra el panel cuando el destino está fuera del componente; el tracker
   * global observa el mismo puntero sin acoplarse a esta interfaz.
   */
  @HostListener('document:pointerdown', ['$event'])
  cerrarFuera(event: PointerEvent): void {
    if (
      this.panelAbierto &&
      !this.elementRef.nativeElement.contains(event.target)
    ) {
      this.panelAbierto = false;
    }
  }

  /**
   * Suspende el socket con la señal compartida y, al volver la actividad,
   * reconstruye contador y bandeja desde la API tenant autoritativa.
   */
  private procesarEstadoActividad(estado: EstadoActividadUsuario): void {
    if (!estado.activo) {
      if (this.socketSuspendido) return;
      this.socketSuspendido = true;
      console.info(
        estado.motivo === 'INACTIVIDAD'
          ? '[Notificaciones] WebSocket desconectado: 15 minutos sin actividad.'
          : '[Notificaciones] WebSocket desconectado: pestaña oculta durante 5 minutos.'
      );
      this.notificacionesServ.desconectar();
      return;
    }

    if (!this.socketSuspendido || !this.requestServ.master?.apiKey) return;
    this.socketSuspendido = false;
    console.info(
      estado.motivo === 'VISIBILIDAD'
        ? '[Notificaciones] Reconectando WebSocket: la pestaña volvió a estar visible.'
        : '[Notificaciones] Reconectando WebSocket: se detectó actividad del usuario.'
    );
    this.notificacionesServ.conectar();
    this.recuperarTrasReconexion();
  }

  /**
   * Mantiene botón y panel dentro de la ventana al cambiar orientación o
   * tamaño, conservando la preferencia de posición más cercana posible.
   */
  @HostListener('window:resize')
  ajustarAlRedimensionar(): void {
    this.posicionX = this.limitarX(this.posicionX);
    this.posicionY = this.limitarY(this.posicionY);
    this.guardarPosicion();
    if (this.panelAbierto) this.programarPosicionPanel();
  }

  /**
   * Compara token y empresa después de cada navegación; ante un cambio limpia
   * la bandeja anterior y conecta la nueva sesión solo si no está suspendida.
   */
  private sincronizarSesion(): void {
    const token = this.requestServ.master?.apiKey || '';
    const idEmpresa =
      this.requestServ.master?.usuario?.ultimaEmpresaId ||
      this.requestServ.master?.usuario?.ultimaEmpresa?.id ||
      '';
    const nuevaClave = `${token}:${idEmpresa}`;
    if (!token || nuevaClave === this.claveSesion) return;

    this.claveSesion = nuevaClave;
    this.panelAbierto = false;
    this.items = [];
    this.total = 0;
    this.contador = 0;
    if (this.actividadTracker.estaActivo) this.notificacionesServ.conectar();
    this.cargarContador();
  }

  /**
   * Actualiza el contador silenciosamente; una indisponibilidad temporal no
   * bloquea el ERP y se recupera con el siguiente evento o reconexión.
   */
  async cargarContador(): Promise<void> {
    try {
      const resultado = await this.notificacionesServ.contarNoLeidas();
      this.contador = resultado.noLeidas;
    } catch (error) {
      // El contador es auxiliar y se recupera sin mostrar alertas globales.
    }
  }

  /**
   * Recarga contador y primera página al recibir una invalidación; Socket.IO
   * nunca se considera fuente confiable del contenido mostrado.
   */
  private async procesarCambioRemoto(): Promise<void> {
    await this.cargarContador();
    if (this.panelAbierto) await this.cargarPrimeraPagina();
  }

  /**
   * Reconstruye el estado al recuperar transporte para absorber cualquier
   * evento perdido mientras el navegador estuvo desconectado.
   */
  private async recuperarTrasReconexion(): Promise<void> {
    await this.cargarContador();
    if (this.panelAbierto) await this.cargarPrimeraPagina();
  }

  /**
   * Abre o cierra el panel con un clic real; el clic posterior a un arrastre se
   * descarta para no abrir accidentalmente la bandeja al reubicar la campana.
   */
  async alternarPanel(event: MouseEvent): Promise<void> {
    event.stopPropagation();
    if (this.omitirClick) return;

    this.panelAbierto = !this.panelAbierto;
    if (this.panelAbierto) {
      this.programarPosicionPanel();
      await this.cargarPrimeraPagina();
    }
  }

  /**
   * Consulta la primera página con el filtro activo y reemplaza el estado local
   * para evitar mezclar resultados de empresas o filtros anteriores.
   */
  async cargarPrimeraPagina(): Promise<void> {
    this.pagina = 1;
    this.cargandoLista = true;
    this.errorLista = '';
    try {
      const resultado = await this.notificacionesServ.listar({
        page: this.pagina,
        limit: this.limite,
        estado: this.estado,
      });
      this.items = resultado.items;
      this.total = resultado.total;
    } catch (error) {
      this.errorLista = error?.message || 'No se pudieron cargar las notificaciones';
    } finally {
      this.cargandoLista = false;
      this.programarPosicionPanel();
    }
  }

  /**
   * Agrega la página siguiente del mismo filtro conservando el orden entregado
   * por SQL y sin duplicar una consulta cuando ya se cargó el total.
   */
  async cargarMas(): Promise<void> {
    if (this.cargandoLista || this.items.length >= this.total) return;
    this.cargandoLista = true;
    try {
      const siguientePagina = this.pagina + 1;
      const resultado = await this.notificacionesServ.listar({
        page: siguientePagina,
        limit: this.limite,
        estado: this.estado,
      });
      this.pagina = siguientePagina;
      this.items = this.items.concat(resultado.items);
      this.total = resultado.total;
    } catch (error) {
      this.errorLista = error?.message || 'No se pudieron cargar más notificaciones';
    } finally {
      this.cargandoLista = false;
      this.programarPosicionPanel();
    }
  }

  /**
   * Cambia entre toda la bandeja y las pendientes y vuelve a consultar desde
   * la primera página para mantener paginación y total coherentes.
   */
  cambiarEstado(estado: EstadoLecturaNotificacionDTO): void {
    if (this.estado === estado) return;
    this.estado = estado;
    this.cargarPrimeraPagina();
  }

  /**
   * Marca el aviso seleccionado antes de navegar cuando sea posible; un fallo
   * de lectura no bloquea la ruta válida de la entidad relacionada.
   */
  async seleccionar(item: NotificacionUsuarioDTO): Promise<void> {
    if (!item.fechaLectura) {
      try {
        await this.notificacionesServ.marcarLeida(item.id);
        item.fechaLectura = new Date().toISOString();
        this.contador = Math.max(0, this.contador - 1);
      } catch (error) {
        // La navegación funcional tiene prioridad sobre el estado auxiliar.
      }
    }
    if (item.rutaFrontend) {
      this.panelAbierto = false;
      await this.router.navigateByUrl(item.rutaFrontend);
    }
  }

  /**
   * Marca todas las entregas visibles en backend y actualiza el snapshot local
   * sin esperar el evento Socket.IO que confirma la misma operación.
   */
  async marcarTodas(): Promise<void> {
    if (!this.contador || this.cargandoAccion) return;
    this.cargandoAccion = true;
    try {
      await this.notificacionesServ.marcarTodasLeidas();
      const fechaLectura = new Date().toISOString();
      this.items.forEach((item) => {
        if (!item.fechaLectura) item.fechaLectura = fechaLectura;
      });
      this.contador = 0;
      if (this.estado === 'NO_LEIDAS') await this.cargarPrimeraPagina();
    } catch (error) {
      this.requestServ.showAlert(error?.message, 'error');
    } finally {
      this.cargandoAccion = false;
    }
  }

  /**
   * Archiva la entrega elegida sin ejecutar la selección de la fila y ajusta
   * contador y total únicamente para el usuario autenticado.
   */
  async archivar(
    item: NotificacionUsuarioDTO,
    event: MouseEvent
  ): Promise<void> {
    event.stopPropagation();
    if (this.cargandoAccion) return;
    this.cargandoAccion = true;
    try {
      await this.notificacionesServ.archivar(item.id);
      this.items = this.items.filter((actual) => actual.id !== item.id);
      this.total = Math.max(0, this.total - 1);
      if (!item.fechaLectura) this.contador = Math.max(0, this.contador - 1);
    } catch (error) {
      this.requestServ.showAlert(error?.message, 'error');
    } finally {
      this.cargandoAccion = false;
      this.programarPosicionPanel();
    }
  }

  /**
   * Inicia el gesto de movimiento desde la posición actual; cerrar el panel
   * evita que su tamaño interfiera mientras se reubica la campana.
   */
  iniciarArrastre(event: PointerEvent): void {
    if (event.button !== 0) return;
    this.arrastrando = true;
    this.desplazado = false;
    this.inicioPunteroX = event.clientX;
    this.inicioPunteroY = event.clientY;
    this.inicioPosicionX = this.posicionX;
    this.inicioPosicionY = this.posicionY;
    if (this.panelAbierto) this.panelAbierto = false;
    const boton = event.currentTarget as HTMLElement;
    if (boton.setPointerCapture) boton.setPointerCapture(event.pointerId);
  }

  /**
   * Desplaza el botón dentro de los límites visibles usando Pointer Events,
   * compatibles con mouse, lápiz y gestos táctiles modernos.
   */
  @HostListener('document:pointermove', ['$event'])
  moverCampana(event: PointerEvent): void {
    if (!this.arrastrando) return;
    const deltaX = event.clientX - this.inicioPunteroX;
    const deltaY = event.clientY - this.inicioPunteroY;
    if (Math.abs(deltaX) + Math.abs(deltaY) > 5) this.desplazado = true;
    if (!this.desplazado) return;

    event.preventDefault();
    this.posicionX = this.limitarX(this.inicioPosicionX + deltaX);
    this.posicionY = this.limitarY(this.inicioPosicionY + deltaY);
  }

  /**
   * Finaliza el gesto, persiste la preferencia por usuario y suprime solamente
   * el clic sintético generado inmediatamente después de un arrastre real.
   */
  @HostListener('document:pointerup')
  finalizarArrastre(): void {
    if (!this.arrastrando) return;
    this.arrastrando = false;
    if (!this.desplazado) return;

    this.guardarPosicion();
    this.omitirClick = true;
    setTimeout(() => {
      this.omitirClick = false;
    });
  }

  /**
   * Devuelve el nombre de Material Icons que representa el nivel persistido
   * sin inferir el comportamiento de la entidad relacionada.
   */
  iconoNivel(item: NotificacionUsuarioDTO): string {
    if (item.nivel === 'ERROR') return 'error_outline';
    if (item.nivel === 'ADVERTENCIA') return 'warning_amber';
    if (item.nivel === 'EXITO') return 'check_circle_outline';
    return 'info_outline';
  }

  /**
   * Expone si hay más filas SQL disponibles para controlar el pie del panel.
   */
  tieneMas(): boolean {
    return this.items.length < this.total;
  }

  /**
   * Crea una clave de preferencia por User.id central para compartir la misma
   * posición entre empresas sin mezclar usuarios del navegador.
   */
  private clavePosicion(): string {
    const idUsuario =
      this.requestServ.master?.usuario?.usuarioId ||
      this.requestServ.master?.usuario?.id ||
      'usuario';
    return `telmah:notificaciones:posicion:${idUsuario}`;
  }

  /**
   * Recupera la preferencia local o usa la esquina inferior derecha del
   * viewport, que coincide con el comportamiento visual del mockup aprobado.
   */
  private restaurarPosicion(): void {
    const posicionGuardada = localStorage.getItem(this.clavePosicion());
    if (posicionGuardada) {
      const posicion = JSON.parse(posicionGuardada);
      this.posicionX = this.limitarX(Number(posicion.x));
      this.posicionY = this.limitarY(Number(posicion.y));
      this.posicionLista = true;
      return;
    }
    this.posicionX = this.limitarX(window.innerWidth - this.tamanoBoton - 26);
    this.posicionY = this.limitarY(window.innerHeight - this.tamanoBoton - 24);
    this.posicionLista = true;
  }

  /**
   * Guarda exclusivamente coordenadas visuales locales; no modifica sesión,
   * empresa ni estado persistente de las notificaciones.
   */
  private guardarPosicion(): void {
    localStorage.setItem(
      this.clavePosicion(),
      JSON.stringify({ x: this.posicionX, y: this.posicionY })
    );
  }

  /**
   * Limita la coordenada horizontal para conservar el botón completo y
   * alcanzable incluso después de reducir el viewport.
   */
  private limitarX(valor: number): number {
    return Math.min(
      Math.max(this.margenViewport, valor),
      window.innerWidth - this.tamanoBoton - this.margenViewport
    );
  }

  /**
   * Limita la coordenada vertical para conservar el botón completo y
   * alcanzable incluso al rotar un dispositivo móvil.
   */
  private limitarY(valor: number): number {
    return Math.min(
      Math.max(this.margenViewport, valor),
      window.innerHeight - this.tamanoBoton - this.margenViewport
    );
  }

  /**
   * Espera a que Angular renderice el panel y calcula el lado con mayor espacio
   * para evitar que la bandeja quede recortada junto a un borde.
   */
  private programarPosicionPanel(): void {
    setTimeout(() => this.posicionarPanel());
  }

  /**
   * Posiciona la bandeja junto a la campana y la restringe al viewport usando
   * las dimensiones reales del panel ya renderizado.
   */
  private posicionarPanel(): void {
    if (!this.panelAbierto || !this.panelRef) return;
    const panel = this.panelRef.nativeElement as HTMLElement;
    const ancho = panel.offsetWidth;
    const alto = panel.offsetHeight;
    const abreIzquierda = this.posicionX + this.tamanoBoton / 2 > window.innerWidth / 2;
    const abreArriba = this.posicionY + this.tamanoBoton / 2 > window.innerHeight / 2;

    const izquierda = abreIzquierda
      ? this.posicionX + this.tamanoBoton - ancho
      : this.posicionX;
    const arriba = abreArriba
      ? this.posicionY - alto - 12
      : this.posicionY + this.tamanoBoton + 12;
    this.panelX = Math.min(
      Math.max(12, izquierda),
      window.innerWidth - ancho - 12
    );
    this.panelY = Math.min(
      Math.max(12, arriba),
      window.innerHeight - alto - 12
    );
  }
}
